"use client"

import {
  useState,
  useRef,
  useEffect,
  useMemo,
  KeyboardEvent,
  ChangeEvent,
} from "react"
import { X, Check, ChevronDown } from "lucide-react"

export interface AutocompleteOption {
  value: string
  label: string
}

interface AutocompleteBaseProps {
  options: AutocompleteOption[]
  placeholder?: string
  disabled?: boolean
  allowCreate?: boolean
  emptyMessage?: string
  className?: string
}

interface AutocompleteSingleProps extends AutocompleteBaseProps {
  multi?: false
  value: string | null
  onChange: (value: string | null) => void
}

interface AutocompleteMultiProps extends AutocompleteBaseProps {
  multi: true
  value: string[]
  onChange: (value: string[]) => void
}

type AutocompleteProps = AutocompleteSingleProps | AutocompleteMultiProps

export function Autocomplete(props: AutocompleteProps) {
  const {
    options,
    placeholder = "",
    disabled = false,
    allowCreate = false,
    emptyMessage = "No options",
    className = "",
  } = props

  const [input, setInput] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedValues = props.multi
    ? props.value
    : props.value
    ? [props.value]
    : []

  const getLabel = (val: string): string => {
    const found = options.find((o) => o.value === val)
    return found ? found.label : val
  }

  const filteredOptions = useMemo(() => {
    const q = input.trim().toLowerCase()
    return options.filter((o) => {
      if (!props.multi && props.value === o.value) return true
      if (props.multi && selectedValues.includes(o.value)) return false
      return !q || o.label.toLowerCase().includes(q)
    })
  }, [options, input, selectedValues, props])

  const showCreateOption =
    allowCreate &&
    input.trim().length > 0 &&
    !options.some((o) => o.label.toLowerCase() === input.trim().toLowerCase()) &&
    !selectedValues.includes(input.trim())

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    setHighlightIdx(0)
  }, [input, isOpen])

  const commitValue = (val: string) => {
    if (props.multi) {
      if (!props.value.includes(val)) {
        props.onChange([...props.value, val])
      }
      setInput("")
    } else {
      props.onChange(val)
      setInput("")
      setIsOpen(false)
    }
  }

  const removeValue = (val: string) => {
    if (props.multi) {
      props.onChange(props.value.filter((v) => v !== val))
    } else {
      props.onChange(null)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const totalOptions = filteredOptions.length + (showCreateOption ? 1 : 0)

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setIsOpen(true)
      setHighlightIdx((i) => (i + 1) % Math.max(totalOptions, 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIdx((i) => (i - 1 + totalOptions) % Math.max(totalOptions, 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightIdx < filteredOptions.length) {
        const opt = filteredOptions[highlightIdx]
        if (opt) commitValue(opt.value)
      } else if (showCreateOption) {
        commitValue(input.trim())
      }
    } else if (e.key === "Backspace" && input === "" && props.multi) {
      const last = props.value[props.value.length - 1]
      if (last) removeValue(last)
    } else if (e.key === "Escape") {
      setIsOpen(false)
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
    setIsOpen(true)
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
    >
      <div
        className={`flex flex-wrap gap-1.5 items-center min-h-[2.5rem] w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-[#595EEE] focus-within:border-transparent ${
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-text"
        }`}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus()
            setIsOpen(true)
          }
        }}
      >
        {props.multi &&
          props.value.map((val) => (
            <span
              key={val}
              className="inline-flex items-center gap-1 rounded-full bg-[#595EEE]/10 text-[#595EEE] text-xs font-medium px-2 py-0.5"
            >
              {getLabel(val)}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  removeValue(val)
                }}
                className="hover:bg-[#595EEE]/20 rounded-full p-0.5"
                aria-label={`Remove ${getLabel(val)}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

        {!props.multi && props.value && input === "" && (
          <span className="text-sm text-gray-900 truncate">{getLabel(props.value)}</span>
        )}

        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={
            (props.multi ? props.value.length === 0 : !props.value) ? placeholder : ""
          }
          disabled={disabled}
          className="flex-1 min-w-[60px] outline-none bg-transparent text-sm placeholder:text-gray-400"
        />

        {!props.multi && props.value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              removeValue(props.value as string)
            }}
            className="hover:bg-gray-100 rounded-full p-0.5 text-gray-400"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {filteredOptions.length === 0 && !showCreateOption && (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</div>
          )}
          {filteredOptions.map((opt, idx) => {
            const isSelected = selectedValues.includes(opt.value)
            const isHighlighted = idx === highlightIdx
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => commitValue(opt.value)}
                onMouseEnter={() => setHighlightIdx(idx)}
                className={`w-full flex items-center justify-between text-left px-3 py-2 text-sm ${
                  isHighlighted ? "bg-[#595EEE]/10" : "hover:bg-gray-50"
                } ${isSelected ? "text-[#595EEE]" : "text-gray-900"}`}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && <Check className="w-4 h-4 shrink-0" />}
              </button>
            )
          })}
          {showCreateOption && (
            <button
              type="button"
              onClick={() => commitValue(input.trim())}
              onMouseEnter={() => setHighlightIdx(filteredOptions.length)}
              className={`w-full text-left px-3 py-2 text-sm border-t border-gray-100 ${
                highlightIdx === filteredOptions.length
                  ? "bg-[#595EEE]/10 text-[#595EEE]"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              + Create "{input.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  )
}
